/**
 * Meta Pixel funnel events — pure builders + a consent-aware fire wrapper.
 *
 * PR3 scope: browser-side ViewContent / AddToCart / InitiateCheckout /
 * AddPaymentInfo with DETERMINISTIC event_ids. The browser is the single
 * source of truth for the event_id; a later server mirror (PR4) must REUSE
 * the exact same id and never recompute it, so Meta dedupes browser↔server.
 * PageView is owned by the PR2 loader; Purchase stays server-side only.
 *
 * event_id formulas (kept in lockstep with the inline mirror in lp-tracking.ts):
 *   viewcontent      : viewcontent:{vid}:{product_id}
 *   addtocart        : addtocart:{vid}:{product_id}:{click_id}
 *   initiatecheckout : initiatecheckout:{vid}:{checkout_slug}:{cart_hash}
 *   addpaymentinfo   : addpaymentinfo:{order_id|payment_intent_id}
 *
 * NOTE: no JS test runner exists in this repo yet, so these helpers are kept
 * small + deterministic and covered by typecheck only. Runtime unit tests are
 * tracked as a follow-up test-infra PR.
 */

import { fireOpenAI } from "@/lib/openai-pixel-fire";

export type FunnelKind =
  | "viewcontent"
  | "addtocart"
  | "initiatecheckout"
  | "addpaymentinfo";

/**
 * Meta event names this funnel forwards server-side (PR4). The /api/capi/event
 * transport gates on this allowlist. PageView is included so the loader's
 * PageView is mirrored to CAPI (shared event_id → Meta dedupes browser↔server),
 * closing the pixel-vs-CAPI coverage gap. Purchase (server webhook) stays out —
 * it has its own /api/capi/push path. The non-Meta mirrors (TikTok/Pinterest) in
 * /api/capi/event skip PageView (Meta-only coverage event).
 */
export const CAPI_ALLOWED_EVENTS: ReadonlySet<string> = new Set([
  "PageView",
  "ViewContent",
  "AddToCart",
  "InitiateCheckout",
  "AddPaymentInfo",
  // Lead — first-class on our own stack, ready for the 5-Day-Challenge and
  // brand-website migrations off GoHighLevel (which owns Lead today at EMQ
  // 8.3). Meta-only: TikTok and Pinterest deliberately skip it, since neither
  // Shopify-parity channel fires a lead event and mirroring one would invent a
  // conversion their optimisers would then chase.
  "Lead",
  // LinkInBioClick: the outbound click on the link-in-bio page, a CUSTOM event
  // (fbq trackCustom), not a standard one. It carries no value and no PII: item
  // id, campaign slug and the platform the visitor came from. Meta-only for the
  // same reason as Lead: neither TikTok nor Pinterest fires a bio-click event,
  // and mirroring one would hand their optimisers a conversion nobody asked
  // for. /api/capi/event already skips both for this name.
  "LinkInBioClick",
]);

/**
 * Meta events this funnel forwards that are CUSTOM, not standard. The transport
 * treats them the same; the list exists so the non-Meta mirrors and any future
 * standard-event validation can tell the two apart in one place.
 */
export const CAPI_META_ONLY_EVENTS: ReadonlySet<string> = new Set([
  "PageView",
  "Lead",
  "LinkInBioClick",
]);

/**
 * Deterministic Lead event id: `lead:{vid}:{source}`.
 *
 * The visitor id (not the email) is the anchor, for two reasons: the browser
 * and the server mirror must derive the SAME id without either of them handling
 * plaintext email twice, and a visitor who corrects a typo in their address must
 * not produce a second Lead. `source` separates genuinely different capture
 * moments on one page (e.g. an exit popup vs the checkout field), so those DO
 * count twice — which is correct, they are different intents.
 */
export function leadEventId(vid: string, source: string): string {
  return `lead:${vid}:${(source || "lp").trim() || "lp"}`;
}

/** Joins a kind + ordered parts into a stable `kind:a:b:c` event id. */
export function funnelEventId(kind: FunnelKind, parts: Array<string | number>): string {
  return [kind, ...parts.map((p) => String(p))].join(":");
}

/**
 * Order-independent, deterministic short hash of the cart's content ids.
 * djb2 → base36. The same set of ids in any order yields the same hash, so the
 * browser and a later server mirror produce identical InitiateCheckout ids.
 */
export function cartHash(contentIds: string[]): string {
  const norm = contentIds
    .map((s) => String(s).trim())
    .filter(Boolean)
    .sort()
    .join("|");
  let h = 5381;
  for (let i = 0; i < norm.length; i++) {
    h = ((h << 5) + h + norm.charCodeAt(i)) >>> 0; // h*33 + c, unsigned
  }
  return h.toString(36);
}

export interface FunnelItem {
  id: string;
  name?: string;
  quantity?: number;
  priceCents?: number;
}

/**
 * Canonical Meta content_id for a product: the trimmed SKU when set, else the
 * product UUID. Mirror of the Go `model.CanonicalContentID` so the browser
 * funnel (Pixel) emits the SAME content_ids the server CAPI does. Used for the
 * funnel events; the Purchase pixel instead reads the immutable per-order
 * snapshot (order_items.content_id).
 */
export function contentIdForProduct(p: {
  sku?: string | null;
  id: string;
}): string {
  const sku = (p.sku || "").trim();
  return sku || p.id;
}

/**
 * Integer cents behind each FunnelCustomData object. Meta's custom_data carries
 * the value in major units (a float); ChatGPT Ads needs integer minor units and
 * must never get them by converting that float. Keyed by object identity so
 * Meta's payload stays byte-identical.
 */
const funnelCents = new WeakMap<object, number>();

export function funnelValueCents(customData: unknown): number | undefined {
  return customData && typeof customData === "object" ? funnelCents.get(customData) : undefined;
}

export interface FunnelCustomData {
  value: number;
  currency: string;
  content_ids: string[];
  content_name: string;
  content_type: "product";
  contents: Array<{ id: string; quantity: number; item_price: number }>;
  num_items: number;
}

/**
 * Builds Meta custom_data from cart items. value/item_price are in major units
 * (divided from cents); num_items is the SUM of quantities (matches the PR1
 * server-side Purchase shape).
 */
export function buildFunnelCustomData(
  items: FunnelItem[],
  totalCents: number,
  currency: string
): FunnelCustomData {
  const content_ids: string[] = [];
  const names: string[] = [];
  const contents = items.map((it) => {
    content_ids.push(it.id);
    if (it.name) names.push(it.name);
    return {
      id: it.id,
      quantity: it.quantity || 1,
      item_price: (it.priceCents || 0) / 100,
    };
  });
  const cd: FunnelCustomData = {
    value: (totalCents || 0) / 100,
    currency: (currency || "USD").toUpperCase(),
    content_ids,
    content_name: names.join(" + ").slice(0, 200),
    content_type: "product",
    contents,
    num_items: items.reduce((s, it) => s + (it.quantity || 1), 0),
  };
  if (Number.isInteger(totalCents)) funnelCents.set(cd, totalCents);
  return cd;
}

/** Reads the LP visitor id cookie (`_lp_vid`) — the shared funnel anchor. */
export function readLpVid(): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|; )_lp_vid=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : "";
}

interface FbqWindow {
  fbq?: (...args: unknown[]) => void;
  __msMetaPixelInit?: boolean;
}

/**
 * Context the server needs (PR4b) to resolve the workspace's Meta credentials
 * and stitch attribution — never any raw PII. fbp/fbc/fbclid live in
 * adIdentifiers (pseudonymous, already captured client-side).
 */
export interface CapiForwardContext {
  adIdentifiers?: Record<string, string>;
  slug?: string;
  productId?: string;
  // Advanced matching: plaintext email typed at checkout. The server SHA-256-
  // hashes it before Meta (never logged/persisted). Used on BOTH paths: off-LP
  // (/checkout) via mirrorCapiEvent, and on the LP via the __msFbq wrapper ctx,
  // where it takes precedence over the slower-to-populate __lpProfile.email.
  email?: string;
}

/**
 * Non-blocking mirror of a browser Pixel event to the server transport
 * (/api/capi/event), so a later server CAPI push (PR4b) can re-send it to Meta
 * with the SAME event_id (browser stays the source of truth — the server never
 * recomputes the id). Uses sendBeacon (falls back to keepalive fetch). Never
 * throws; failures are silently dropped — this must never affect checkout.
 *
 * Only ever called from fireMetaPixel AFTER the pixel actually fired, so it
 * inherits the consent gate: no pixel → no server forward.
 */
export function mirrorCapiEvent(
  eventName: string,
  eventId: string,
  customData: FunnelCustomData | Record<string, unknown>,
  ctx: CapiForwardContext
): void {
  if (typeof window === "undefined" || typeof navigator === "undefined") return;
  try {
    const payload: Record<string, unknown> = {
      event_name: eventName,
      event_id: eventId,
      custom_data: customData,
      ad_identifiers: ctx.adIdentifiers || {},
      context: { slug: ctx.slug, product_id: ctx.productId },
    };
    // Advanced matching: attach the typed email (server hashes it before Meta).
    // This off-LP path does ONLY the direct beacon (no capi_fwd backup), so no
    // plaintext email is persisted server-side.
    if (ctx.email) payload.user = { email: ctx.email };
    const cents = funnelValueCents(customData);
    if (cents !== undefined) payload.value_cents = cents;
    const body = JSON.stringify(payload);
    const url = "/api/capi/event";
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      void fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* non-blocking: a failed mirror must never break checkout */
  }
}

/**
 * Fires a Meta Pixel track event — ONLY when the consent-gated PR2 loader has
 * actually initialised the pixel (`window.__msMetaPixelInit`). If consent
 * wasn't granted the pixel never loaded, so this is a silent no-op. Never
 * throws: tracking must never break the checkout flow.
 *
 * When `forward` is supplied, the same event is mirrored to the server
 * transport (PR4) — but only on the path where the pixel actually fired, so
 * the server forward is consent-gated for free.
 */
export function fireMetaPixel(
  eventName: string,
  customData: FunnelCustomData | Record<string, unknown>,
  eventId: string,
  forward?: CapiForwardContext
): void {
  if (typeof window === "undefined") return;
  // ChatGPT Ads rides every Meta funnel fire with the SAME event id. Before the
  // Meta guard: a workspace may run ChatGPT Ads without Meta. The OpenAI loader
  // is itself consent-gated and no-ops until it has initialised.
  const cents = funnelValueCents(customData);
  const cdObj = customData as { currency?: unknown; content_ids?: unknown };
  fireOpenAI(eventName, {
    cents,
    currency: typeof cdObj.currency === "string" ? cdObj.currency : undefined,
    contentIds: Array.isArray(cdObj.content_ids) ? cdObj.content_ids.map(String) : undefined,
  }, eventId);
  const w = window as unknown as FbqWindow & {
    __msFbq?: (name: string, cd: unknown, eid: string, ctx: Record<string, unknown>) => void;
  };
  if (!w.__msMetaPixelInit || typeof w.fbq !== "function") return;
  // On the landing page, prefer the LP's __msFbq wrapper: it fires the pixel AND
  // does a DUAL server forward — the direct /api/capi/event beacon PLUS a
  // capi_fwd backup via the lp-events batch (re-posted server-side if the beacon
  // drops) with fresh _fbc/_fbp re-read at fire time. A lone beacon (our
  // mirrorCapiEvent) can drop → server event lost (browser-only). So when the
  // robust wrapper is present, use it. Off the LP (e.g. /checkout) it isn't, so
  // fall back to fbq + mirrorCapiEvent.
  //
  // The caller's `email` rides along in the ctx (F7): the wrapper otherwise only
  // knows `window.__lpProfile.email`, which at AddPaymentInfo time is not yet
  // populated on the island path — that gap is why Events Manager lists NO email
  // key on AddPaymentInfo / InitiateCheckout at all. _msCapiForward gives the
  // caller-passed address precedence and strips it back out of the forwarded
  // `context` object, so plaintext never enters the persisted capi_fwd batch.
  if (forward && typeof w.__msFbq === "function") {
    try {
      w.__msFbq(eventName, customData, eventId, {
        slug: forward.slug,
        product_id: forward.productId,
        email: forward.email,
        value_cents: cents,
      });
    } catch {
      /* tracking must never break checkout */
    }
    return;
  }
  try {
    w.fbq("track", eventName, customData, { eventID: eventId });
  } catch {
    /* tracking must never break checkout */
  }
  if (forward) mirrorCapiEvent(eventName, eventId, customData, forward);
}

/** localStorage key marking that the browser Purchase pixel fired for an order. */
function purchaseGuardKey(orderId: string): string {
  return `ms_purchase_fired:${orderId}`;
}

/**
 * Fires the browser-side Meta Pixel Purchase ONCE per order, for Meta dedup.
 *
 * The server is the Purchase source of truth — it pushes the CAPI Purchase from
 * the Stripe/PayPal webhook with `eventID = order_id`. This helper fires the
 * matching BROWSER Pixel Purchase with the SAME `eventID` (order_id), so Meta
 * dedupes the two into one "Deduplicated" Purchase. It deliberately does NOT
 * forward to /api/capi/event (no `forward` arg) — that server transport is for
 * the funnel events only; a Purchase forward would create a third path.
 *
 * Guard: a localStorage key (per order_id) so a reload / second tab / the
 * upsell→success navigation never double-fires. The guard is only set AFTER
 * fireMetaPixel ran, and fireMetaPixel itself no-ops unless the consent-gated
 * pixel actually initialised — so a consent-denied visitor stays unguarded and
 * un-tracked. Never throws.
 */
export function firePurchaseOnce(
  orderId: string,
  customData: FunnelCustomData | Record<string, unknown>
): void {
  if (typeof window === "undefined" || !orderId) return;
  const key = purchaseGuardKey(orderId);
  try {
    if (window.localStorage.getItem(key)) return; // already fired for this order
  } catch {
    /* localStorage unavailable (private mode quota) — fall through and fire */
  }

  // No `forward` arg: browser Pixel only. eventID === order_id matches the
  // server CAPI Purchase, so Meta dedupes by event_id.
  fireMetaPixel("Purchase", customData, orderId);

  try {
    window.localStorage.setItem(key, "1");
  } catch {
    /* best-effort guard; a failed write just risks a re-fire on reload */
  }
}

/**
 * Fires a Meta Lead ONCE per (visitor, source), browser pixel + server mirror
 * with the SAME deterministic id so Meta dedupes the pair into one Lead.
 *
 * Today the Lead event on this dataset belongs to GoHighLevel (EMQ 8.3); our
 * own LP email capture is negligible. This exists so the 5-Day-Challenge and
 * brand-website migrations OFF GoHighLevel have a first-class Lead already
 * running on our stack, with the same identity spine as every other event.
 *
 * The guard is sessionStorage, not localStorage: a returning visitor on a later
 * day IS a new lead moment, while a reload or a second tab within the session
 * is not. Never throws — a lead form must never break on a tracking error.
 */
export function fireLeadOnce(
  vid: string,
  source: string,
  forward: CapiForwardContext & { firstName?: string; lastName?: string },
  customData: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined" || !vid) return;
  const eventId = leadEventId(vid, source);
  const key = `ms_lead_fired:${eventId}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
  } catch {
    /* storage unavailable (private mode) — fall through and fire */
  }

  // Same wrapper preference as the funnel events: on an LP the __msFbq wrapper
  // adds the reliable capi_fwd backup, off-LP we fall back to fbq + beacon.
  fireMetaPixel("Lead", customData, eventId, forward);
  // Names ride the direct beacon only (mirrorCapiEvent), so plaintext never
  // enters the persisted capi_fwd batch — same rule the email follows.
  if (forward.firstName || forward.lastName) {
    mirrorLeadNames(eventId, customData, forward);
  }

  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    /* best-effort guard */
  }
}

/**
 * Sends the Lead's name fields on the direct beacon. Separate from
 * mirrorCapiEvent because only Lead has names, and because this must never
 * touch the capi_fwd batch path (which persists server-side).
 */
function mirrorLeadNames(
  eventId: string,
  customData: Record<string, unknown>,
  forward: CapiForwardContext & { firstName?: string; lastName?: string },
): void {
  if (typeof navigator === "undefined") return;
  try {
    const user: Record<string, string> = {};
    if (forward.email) user.email = forward.email;
    if (forward.firstName) user.first_name = forward.firstName;
    if (forward.lastName) user.last_name = forward.lastName;
    const body = JSON.stringify({
      event_name: "Lead",
      event_id: eventId,
      custom_data: customData,
      ad_identifiers: forward.adIdentifiers || {},
      context: { slug: forward.slug, product_id: forward.productId },
      user,
    });
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/api/capi/event", new Blob([body], { type: "application/json" }));
    } else {
      void fetch("/api/capi/event", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* non-blocking */
  }
}
